// @givmo/app-charity — the FRAME a navigation carries: the borrowed row's origin (Org Machinery
// OM-18; D-CPO-40 (3) — the shell contract v3 amendment). The company console's twin, verbatim.
//
// A module's row may BORROW one page of another module, or an Organization row: followed, the page
// wears the BORROWER's frame with that row lit. Which sidebar the row was followed in rides the
// router's HISTORY STATE on the sidebar's own navigations — the `settingsOrigin` precedent
// (`settings-origin.ts`): a key that lives with its history entry (a reload keeps it) and dies at
// the NEXT navigation (TanStack writes the state it is given; an omitted `state` is `{}`), never a
// storage key, never a module-level recorder.
//
// TRI-STATE, because the package's rule (`sidebarFor`'s `borrowedFrom`) reads three facts from it:
//   a slug   — the module whose sidebar the row was followed in; it frames the page iff it borrows
//              it, and is inert on every other page (a LINK row followed there keeps the
//              destination's own frame);
//   `null`   — EXPLICITLY none, a module switch: the popup's tiles, the wordmark's landing, the
//              doorway, the Apps squares, the roster's door, and every SIGN-IN landing (OM-18-R1;
//              `SIGN_IN_LANDING_STATE`, below). The destination opens in its OWN frame and the
//              last-module hint is NOT consulted;
//   absent   — a navigation that carried nothing (a module's own `<Link>`, a typed address): the
//              last-module hint decides, and the shell keeps the hint equal to the frame inside a
//              borrowed subtree (`useRememberCurrentModule`), so an in-page hop keeps the frame.

import { useRouterState } from '@tanstack/react-router';

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** The frame this navigation carried — a slug, an explicit `null`, or absent (OM-18). */
    sidebarFrame?: string | null;
  }
}

/**
 * A carried frame under `key` in a location's state, read tolerantly: a non-empty slug; `null` when
 * carried as none; `undefined` when the key is absent or malformed (a pre-deploy entry, a typed
 * address, a reload that lost state).
 */
export function carriedFrameOf(state: unknown, key: string): string | null | undefined {
  if (typeof state !== 'object' || state === null || !(key in state)) return undefined;
  const frame = (state as Record<string, unknown>)[key];
  if (frame === null) return null;
  if (typeof frame !== 'string') return undefined;
  const trimmed = frame.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** The frame a location's state carries for the SIDEBAR (the key the sidebar's rows write). */
export function sidebarFrameOf(state: unknown): string | null | undefined {
  return carriedFrameOf(state, 'sidebarFrame');
}

/**
 * The state every sign-in LANDING carries (Org Machinery OM-18-R1): the bounce an already-signed-in
 * visitor takes off a sign-in door and the navigation a completed sign-in makes, on the console's own
 * doors and the per-module doors alike. Signing in is a fresh entry — a module switch — so the page
 * it lands on opens in its own frame, never one a stale last-module hint would lend by borrowing it.
 */
export const SIGN_IN_LANDING_STATE = { sidebarFrame: null } as const;

/** The frame THIS location carries — handed to the rule as `borrowedFrom`. */
export function useSidebarFrame(): string | null | undefined {
  return useRouterState({ select: (state) => sidebarFrameOf(state.location.state) });
}
